import { createSlice } from "@reduxjs/toolkit";
import {
    sendAIMessage,
    startAISession,
    endAISession,
    getAIConversationHistory
} from './thunk';

export const initialState = {
    messages: [] as Array<{
        id: string;
        role: 'user' | 'assistant';
        content: string;
        timestamp: string;
        conversation_id?: string;
    }>,
    sessionId: null as string | null,
    loading: false,
    error: null as string | null,
    conversationHistory: [] as any[]
};

const aiAssistantSlice = createSlice({
    name: 'aiAssistant',
    initialState,
    reducers: {
        clearMessages: (state) => {
            state.messages = [];
            state.error = null;
        },
        addUserMessage: (state, action) => {
            state.messages.push({
                id: `user-${Date.now()}`,
                role: 'user',
                content: action.payload,
                timestamp: new Date().toISOString()
            });
        },
        addAssistantMessage: (state, action) => {
            state.messages.push({
                id: `assistant-${Date.now()}`,
                role: 'assistant',
                content: action.payload.content,
                timestamp: new Date().toISOString(),
                conversation_id: action.payload.conversation_id
            });
        },
        setSessionId: (state, action) => {
            state.sessionId = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(sendAIMessage.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(sendAIMessage.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.answer) {
                    state.messages.push({
                        id: `assistant-${Date.now()}`,
                        role: 'assistant',
                        content: action.payload.answer,
                        timestamp: new Date().toISOString(),
                        conversation_id: action.payload.conversation_id
                    });
                }
                if (action.payload?.session_id) {
                    state.sessionId = action.payload.session_id;
                }
            })
            .addCase(sendAIMessage.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message || 'Failed to send message';
            })
            .addCase(startAISession.pending, (state) => {
                state.loading = true;
            })
            .addCase(startAISession.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload?.session_id) {
                    state.sessionId = action.payload.session_id;
                }
            })
            .addCase(startAISession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message || 'Failed to start session';
            })
            .addCase(endAISession.pending, (state) => {
                state.loading = true;
            })
            .addCase(endAISession.fulfilled, (state) => {
                state.loading = false;
                state.sessionId = null;
            })
            .addCase(endAISession.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message || 'Failed to end session';
            })
            .addCase(getAIConversationHistory.pending, (state) => {
                state.loading = true;
            })
            .addCase(getAIConversationHistory.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.conversationHistory = action.payload;
                }
            })
            .addCase(getAIConversationHistory.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error?.message || 'Failed to get conversation history';
            });
    }
});

export const { clearMessages, addUserMessage, addAssistantMessage, setSessionId, clearError } = aiAssistantSlice.actions;

export default aiAssistantSlice.reducer;

